import { SystemIcon } from '@storeblocks/icon';
import { AnimatePresence, motion } from 'framer-motion';
import { type FC, useMemo } from 'react';
import styled from 'styled-components';

import { Organization } from '@/api/organizations/models/Organization';
import { Show } from '@/components/Show';
import { useTexts } from '@/hooks/useTexts';

interface Props {
  organizations: Organization[];
  collapseSize: number;
  expanded: boolean;
}

export const OrganizationList: FC<Props> = ({
  organizations,
  collapseSize,
  expanded,
}) => {
  const texts = useTexts();

  const visibleOrganizations = useMemo(() => {
    if (expanded) {
      return organizations;
    }
    return organizations.slice(0, collapseSize);
  }, [organizations, collapseSize, expanded]);

  return (
    <>
      <Show when={organizations.length === 0}>
        <span className="font-body">{texts.home.organizations.empty}</span>
      </Show>
      <AnimatePresence initial={false}>
        {visibleOrganizations.map((organization) => (
          <AnimatedLink
            transition={{ ease: 'easeInOut' }}
            initial={{ height: 0, padding: 0, opacity: 0 }}
            animate={{
              height: '25px',
              padding: '16px 8px',
              opacity: 1,
            }}
            exit={{ height: 0, padding: 0, opacity: 0 }}
            key={organization.organizationId}
            href={`/${organization.organizationId}`}
          >
            <span>{organization.organizationName}</span>
            <SystemIcon name="arrow_right" aria-hidden="true" isPhosphor />
          </AnimatedLink>
        ))}
      </AnimatePresence>
    </>
  );
};

const AnimatedLink = styled(motion.a)`
  color: var(--p-color-text-primary);
  border-top: 1px solid var(--p-color-stroke-subtle-0);
  text-decoration: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 25px;
  padding: 16px 8px;
  line-height: 24px;

  &:hover {
    background-color: var(--p-color-surface-1-hover);
    color: var(--p-color-text-primary-pressed);
  }

  &:last-of-type {
    border-bottom: 1px solid var(--p-color-stroke-subtle-0);
  }
`;
